import { useCallback, useMemo, useRef, useState } from 'react';
import { C, CT, soft } from '@parsina/framework/tokens';
import { Button } from '@parsina/framework';
import { GripVertical, ChevronDown, ChevronLeft } from 'lucide-react';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import {
  resolveVisitLayout, isDefaultVisitLayout,
  reorderRecordSections, setRecordSectionVisible, setRecordSectionCollapsed,
  setWorkTabVisible, setDefaultWorkTab, setCopilotTabVisible, setDefaultCopilotTab,
} from '@parsina/framework/clinical/src/screens/Visit/visitLayout';

// ─────────────────────────────────────────────────────────────────────────────
// «صفحه ویزیت» — the layout editor, phone-shaped.
//
// The desktop editor puts a row's three decisions side by side, which a 390px
// screen cannot hold: the row wraps, and its ▲/▼ buttons are below the 44px
// touch floor. So this is a genuine fork of the SURFACE, not of the logic —
// every control calls the same pure operation in screens/Visit/visitLayout.js
// and hands the same patch to the same mutation. The two editors cannot mean
// different things by an order or a hidden section.
//
// Three changes the phone makes:
//   1. One group at a time (a segmented control), because the three columns of
//      the visit page do not fit side by side and do not need to.
//   2. Reordering is a DRAG, the phone's gesture for it — with the keyboard
//      path kept (the handle is a real button; ↑/↓ move the row), because a
//      drag-only reorder is unreachable for anyone not using a finger.
//   3. Two decisions per row instead of three columns of checkboxes: a switch
//      shows or hides, and the state line under the title IS the «باز/بسته در
//      شروع» control rather than a second checkbox competing for the width.
// ─────────────────────────────────────────────────────────────────────────────

const ROW_MIN_H = '3.5rem';           // 56px — a comfortable drag target

const rowStyle = (dragging, lifted) => ({
  display: 'flex', alignItems: 'center', gap: '0.625rem',
  minHeight: ROW_MIN_H, padding: '0.5rem 0',
  borderBottom: `1px solid ${C.border}`,
  background: lifted ? C.card : 'transparent',
  boxShadow: lifted ? '0 8px 20px rgba(26,35,50,0.16)' : 'none',
  borderRadius: lifted ? '0.625rem' : 0,
  // The lifted row follows the finger; the others slide under it.
  transition: dragging ? 'none' : 'transform .16s ease',
  position: 'relative',
  touchAction: dragging ? 'none' : 'auto',
});

/**
 * The show/hide switch, as a TRAILING control.
 *
 * The kit's `Toggle` is a full-width ROW — label on one side, switch on the
 * other, its own padding and bottom rule — so it cannot sit at the end of a row
 * that already has a title, a state line and its own rule. This is the same
 * switch, drawn to the same spec (44×24 track, 18px thumb, navy on, #d1d5db
 * off) with a 44px hit area around it, so the two read as one control even
 * though only one of them is the kit's.
 *
 * It carries its own accessible name: the row's `aria-label` names the SECTION,
 * which is not what «is it shown» is asking.
 */
const ShowSwitch = ({ checked, disabled, onChange, label, testId }) => {
  const [ring, setRing] = useState(false);
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      data-testid={testId}
      onClick={() => !disabled && onChange(!checked)}
      onFocus={(e) => {
        let visible = true;
        try { visible = e.target.matches(':focus-visible'); } catch { /* older engine */ }
        setRing(visible);
      }}
      onBlur={() => setRing(false)}
      style={{
        // 44px of finger, 24px of switch.
        minWidth: '2.75rem', minHeight: '2.75rem', flexShrink: 0,
        display: 'grid', placeItems: 'center',
        background: 'none', border: 'none', padding: 0,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <span
        aria-hidden
        style={{
          width: '2.75rem', height: '1.5rem', borderRadius: '999px',
          border: `1px solid ${checked ? 'transparent' : '#cbd2dc'}`,
          background: checked ? C.navy : '#d1d5db',
          boxShadow: ring ? `0 0 0 3px ${soft(C.navy, 0.45)}` : 'none',
          transition: 'background .25s, box-shadow .15s',
          position: 'relative', display: 'block',
        }}
      >
        <span style={{
          position: 'absolute', top: '0.125rem',
          width: '1.125rem', height: '1.125rem', borderRadius: '50%',
          background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
          insetInlineEnd: checked ? '0.125rem' : '1.375rem',
          transition: 'inset-inline-end .25s',
        }} />
      </span>
    </button>
  );
};

/** The «باز در شروع» / «بسته در شروع» state line — a control, not a label. */
const OpenStateButton = ({ collapsed, disabled, onToggle, testId }) => {
  const { t } = useTranslation();
  // ChevronLeft bare, not a mirrored one: it is how every «go deeper» affordance
  // in the mobile sheets is drawn (MobileVisitRecordSheet), and the panel is RTL.
  const Icon = collapsed ? ChevronLeft : ChevronDown;
  return (
    <Button
      variant="ghost" size="sm"
      className="min-h-11 h-auto gap-1 px-0 py-0 hover:bg-transparent justify-start"
      data-testid={testId}
      disabled={disabled}
      aria-pressed={!collapsed}
      onClick={onToggle}
      style={{ color: collapsed ? C.textMuted : CT.accentInk }}
    >
      <Icon size={12} aria-hidden />
      <span style={{ fontSize: '0.6875rem', fontWeight: 600 }}>
        {collapsed ? t('settings.visit.closedAtStart') : t('settings.visit.openAtStart')}
      </span>
    </Button>
  );
};

/** «تب اول» — one tab per group opens the visit, so this is a radio. */
const FirstTabButton = ({ selected, disabled, onSelect, testId, name }) => {
  const { t } = useTranslation();
  return (
    <label
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
        minHeight: '2.75rem', padding: '0 0.625rem', borderRadius: '0.5rem',
        border: `1.5px solid ${selected ? C.navy : C.border}`,
        background: selected ? soft(C.navy, 0.06) : 'transparent',
        color: selected ? C.textPrimary : C.textSecondary,
        fontSize: '0.6875rem', fontWeight: selected ? 700 : 600,
        opacity: disabled ? 0.55 : 1, cursor: disabled ? 'not-allowed' : 'pointer',
        flexShrink: 0, whiteSpace: 'nowrap',
      }}
    >
      <input
        type="radio" name={name} checked={selected} disabled={disabled}
        onChange={() => onSelect()} data-testid={testId}
        style={{ accentColor: C.navy, width: '0.875rem', height: '0.875rem', margin: 0 }}
      />
      {t('settings.visit.openFirst')}
    </label>
  );
};

/**
 * @param settings  the stored (sparse) `doctor.settings.visitLayout`
 * @param onChange  (patch) => Promise — persists the keys the patch names
 * @param onReset   () => Promise — back to the shipped page
 */
export default function VisitLayoutEditorMobile({ settings, onChange, onReset, testId = 'settings-visit' }) {
  const { t } = useTranslation();
  const [group, setGroup] = useState('record');
  const layout = resolveVisitLayout(settings);
  const pristine = isDefaultVisitLayout(settings);
  const apply = useCallback((patch) => {
    if (patch && Object.keys(patch).length) onChange(patch);
  }, [onChange]);

  const sections = layout.recordSections;

  // ── Drag to reorder ────────────────────────────────────────────────────────
  //
  // Pointer Events, not HTML5 drag-and-drop: the latter does not exist on
  // touch. Rows in this list are one height, so the drop index is the offset in
  // whole rows — no per-row measuring, and no dependency.
  //
  // `drag` is {id, from, to, dy}. It is LOCAL until release: the list shows the
  // move while the finger is down and only then does one patch go to the
  // server, so a drag across four rows is one write, not four.
  const [drag, setDrag] = useState(null);
  const rowHeightRef = useRef(56);

  const startDrag = useCallback((e, id, from) => {
    const row = e.currentTarget.closest('[data-row]');
    if (row) rowHeightRef.current = row.getBoundingClientRect().height || 56;
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* not captured; the move handler still fires */ }
    setDrag({ id, from, to: from, dy: 0, y0: e.clientY, pointerId: e.pointerId });
  }, []);

  const moveDrag = useCallback((e) => {
    setDrag((d) => {
      if (!d || (d.pointerId != null && e.pointerId !== d.pointerId)) return d;
      const dy = e.clientY - d.y0;
      const steps = Math.round(dy / (rowHeightRef.current || 56));
      const to = Math.max(0, Math.min(sections.length - 1, d.from + steps));
      return { ...d, dy, to };
    });
  }, [sections.length]);

  const endDrag = useCallback(() => {
    setDrag((d) => {
      if (d && d.to !== d.from) apply(reorderRecordSections(settings, d.id, d.to));
      return null;
    });
  }, [apply, settings]);

  // Where a row sits while another is being dragged over it.
  const shiftFor = useCallback((index) => {
    if (!drag || index === drag.from) return 0;
    const h = rowHeightRef.current || 56;
    if (drag.from < drag.to && index > drag.from && index <= drag.to) return -h;
    if (drag.from > drag.to && index < drag.from && index >= drag.to) return h;
    return 0;
  }, [drag]);

  // The keyboard path to the same reorder — ↑/↓ on the focused handle.
  const onHandleKeyDown = useCallback((e, id, index) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    apply(reorderRecordSections(settings, id, index + (e.key === 'ArrowUp' ? -1 : 1)));
  }, [apply, settings]);

  const groupItems = useMemo(() => ([
    { id: 'record', label: t('settings.visit.record.short') },
    { id: 'work', label: t('settings.visit.work.short') },
    { id: 'copilot', label: t('settings.visit.copilot.short') },
  ]), [t]);

  return (
    <div data-testid={testId}>
      {/* One group at a time — the three columns of the visit page. */}
      <div
        role="tablist"
        aria-label={t('settings.visit.title')}
        data-testid={`${testId}-groups`}
        style={{
          display: 'flex', gap: '0.1875rem', padding: '0.1875rem',
          background: CT.surface2, border: `1px solid ${C.border}`,
          borderRadius: '0.625rem', marginBottom: '0.75rem',
        }}
      >
        {groupItems.map((item) => {
          const on = group === item.id;
          return (
            <Button
              key={item.id} variant="ghost" role="tab" aria-selected={on}
              onClick={() => setGroup(item.id)}
              data-testid={`${testId}-group-${item.id}`}
              className="flex-1 min-h-11 rounded-md px-1"
              style={{
                background: on ? C.card : 'transparent',
                color: on ? C.textPrimary : C.textMuted,
                fontWeight: on ? 700 : 600, fontSize: '0.75rem',
                boxShadow: on ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
              }}
            >
              {item.label}
            </Button>
          );
        })}
      </div>

      <p style={{ margin: '0 0 0.5rem', fontSize: '0.6875rem', color: C.textMuted, lineHeight: 1.7 }}>
        {group === 'record' ? t('settings.visit.record.hintMobile')
          : group === 'work' ? t('settings.visit.work.hint')
            : t('settings.visit.copilot.hint')}
      </p>

      {/* ── پرونده بیمار ── */}
      {group === 'record' && (
        <div data-testid={`${testId}-record`}>
          {sections.map((section, index) => {
            const lifted = drag?.id === section.id;
            return (
              <div
                key={section.id}
                data-row
                role="group"
                aria-label={t(section.labelKey)}
                data-testid={`${testId}-record-${section.id}`}
                style={{
                  ...rowStyle(Boolean(drag), lifted),
                  transform: lifted ? `translateY(${drag.dy}px)` : `translateY(${shiftFor(index)}px)`,
                  zIndex: lifted ? 2 : 1,
                }}
              >
                <Button
                  variant="ghost" size="icon"
                  className="min-h-11 min-w-11 shrink-0 cursor-grab active:cursor-grabbing"
                  style={{ touchAction: 'none', color: lifted ? C.navy : C.textMuted }}
                  data-testid={`${testId}-record-${section.id}-handle`}
                  aria-label={t('settings.visit.reorderAria', { title: t(section.labelKey) })}
                  onPointerDown={(e) => startDrag(e, section.id, index)}
                  onPointerMove={moveDrag}
                  onPointerUp={endDrag}
                  onPointerCancel={endDrag}
                  onKeyDown={(e) => onHandleKeyDown(e, section.id, index)}
                >
                  <GripVertical size={18} aria-hidden />
                </Button>

                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
                  <span style={{
                    fontSize: '0.875rem', fontWeight: 600,
                    color: section.locked ? CT.dangerInk : C.textPrimary,
                    opacity: section.visible ? 1 : 0.6,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {t(section.labelKey)}
                  </span>
                  {section.visible ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap' }}>
                      {section.locked && (
                        <span style={{ fontSize: '0.6875rem', color: C.textMuted }}>{t('settings.visit.alwaysShown')}</span>
                      )}
                      <OpenStateButton
                        collapsed={section.collapsed}
                        testId={`${testId}-record-${section.id}-open`}
                        onToggle={() => apply(setRecordSectionCollapsed(settings, section.id, !section.collapsed))}
                      />
                    </span>
                  ) : (
                    <span style={{ fontSize: '0.6875rem', color: C.textMuted }}>{t('settings.visit.hidden')}</span>
                  )}
                </div>

                <ShowSwitch
                  testId={`${testId}-record-${section.id}-visible`}
                  label={t('settings.visit.showAria', { title: t(section.labelKey) })}
                  checked={section.visible}
                  disabled={Boolean(section.locked)}
                  onChange={(on) => apply(setRecordSectionVisible(settings, section.id, on))}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* ── ناحیه کار ── */}
      {group === 'work' && (
        <div data-testid={`${testId}-work`}>
          {layout.workTabs.map((tab) => (
            <div
              key={tab.id} role="group" aria-label={t(tab.labelKey)}
              data-testid={`${testId}-work-${tab.id}`}
              style={rowStyle(false, false)}
            >
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
                <span style={{ fontSize: '0.875rem', fontWeight: 600, color: C.textPrimary, opacity: tab.visible ? 1 : 0.6 }}>
                  {t(tab.labelKey)}
                </span>
                <span style={{ fontSize: '0.6875rem', color: C.textMuted }}>
                  {tab.locked ? t('settings.visit.alwaysShown') : tab.visible ? '' : t('settings.visit.hidden')}
                </span>
              </div>
              <FirstTabButton
                name={`${testId}-work-default`}
                selected={layout.defaultWorkTab === tab.id}
                disabled={!tab.visible}
                testId={`${testId}-work-${tab.id}-default`}
                onSelect={() => apply(setDefaultWorkTab(settings, tab.id))}
              />
              <ShowSwitch
                testId={`${testId}-work-${tab.id}-visible`}
                label={t('settings.visit.showAria', { title: t(tab.labelKey) })}
                checked={tab.visible}
                disabled={Boolean(tab.locked)}
                onChange={(on) => apply(setWorkTabVisible(settings, tab.id, on))}
              />
            </div>
          ))}
        </div>
      )}

      {/* ── دستیار بالینی ── */}
      {group === 'copilot' && (
        <div data-testid={`${testId}-copilot`}>
          {layout.copilotTabs.map((tab) => {
            const lastVisible = tab.visible && layout.copilotTabs.filter((x) => x.visible).length === 1;
            return (
              <div
                key={tab.id} role="group" aria-label={t(tab.labelKey)}
                data-testid={`${testId}-copilot-${tab.id}`}
                style={rowStyle(false, false)}
              >
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
                  <span style={{ fontSize: '0.875rem', fontWeight: 600, color: C.textPrimary, opacity: tab.visible ? 1 : 0.6 }}>
                    {t(tab.labelKey)}
                  </span>
                  <span style={{ fontSize: '0.6875rem', color: C.textMuted }}>
                    {lastVisible ? t('settings.visit.lastTab') : tab.visible ? '' : t('settings.visit.hidden')}
                  </span>
                </div>
                <FirstTabButton
                  name={`${testId}-copilot-default`}
                  selected={layout.defaultCopilotTab === tab.id}
                  disabled={!tab.visible}
                  testId={`${testId}-copilot-${tab.id}-default`}
                  onSelect={() => apply(setDefaultCopilotTab(settings, tab.id))}
                />
                <ShowSwitch
                  testId={`${testId}-copilot-${tab.id}-visible`}
                  label={t('settings.visit.showAria', { title: t(tab.labelKey) })}
                  checked={tab.visible}
                  disabled={lastVisible}
                  onChange={(on) => apply(setCopilotTabVisible(settings, tab.id, on))}
                />
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginTop: '0.75rem' }}>
        <span style={{ fontSize: '0.6875rem', color: C.textMuted }}>
          {pristine ? t('settings.visit.isDefault') : t('settings.visit.isCustom')}
        </span>
        <Button
          variant="outline" size="sm" className="min-h-11 shrink-0"
          disabled={pristine}
          data-testid={`${testId}-reset`}
          onClick={() => onReset()}
        >
          {t('settings.visit.resetShort')}
        </Button>
      </div>

      <p style={{ margin: '0.75rem 0 0', fontSize: '0.6875rem', color: C.textMuted, lineHeight: 1.7, background: soft(C.navy, 0.04), borderRadius: '0.5rem', padding: '0.5rem 0.75rem' }}>
        {t('settings.visit.safetyNote')}
      </p>
    </div>
  );
}
